import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  ArrowDownNarrowWide,
  ArrowUpNarrowWide,
  Download,
  Eye,
  Mail,
  Pencil,
  Plus,
  Printer,
  Trash2,
} from "lucide-react";

import { createAxiosInstance } from "api/axiosConfig";
import { STAFF_MEMBERS_CONTROLER_URL } from "api/Service";
import emptyDocs from "assets/images/empty-docs.svg";
import { Button } from "components/ui/Button";
import { DataLoadError } from "components/ui/DataLoadError";
import { DataTable } from "components/ui/DataTable";
import { Pill, PILL_COLORS } from "components/ui/Pill";
import { useDocumentTypes } from "features/documents/hooks/useDocumentTypes";
import { getDeaspCertificateStatusDisplay } from "features/healthlink/utils/deaspCertificateStatus";
import {
  formatDocumentTypeLabel,
  getDocumentTypeIcon,
} from "utils/documentTypeIcons";

import {
  isDeaspCertificateRow,
  isUploadedDocumentRow,
} from "../../utils/allDocumentsRows";
import {
  formatDocumentDate,
  formatStatusLabel,
} from "../../utils/documentFormatters";
import styles from "./DocumentList.module.css";

/*
 * "and", not a comma, when both extra sources of "All Documents" failed: the sentence it lands in
 * is "We could not load …".
 */
const joinSourceLabels = (labels) =>
  labels.length > 1
    ? `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`
    : labels[0];

const FILTER_GROUPS = [
  {
    id: "status",
    label: "Status",
    type: "multi",
    options: [
      { value: "NEW", label: "New" },
      { value: "IN_REVIEW", label: "In Review" },
      { value: "REVIEWED", label: "Reviewed" },
      { value: "UPLOADED", label: "Uploaded" },
      { value: "SYSTEM_GENERATED", label: "System Generated" },
    ],
  },
];

/*
 * One primary icon button plus the overflow menu. The action list holds two primaries — Download
 * for an upload, View for a row merged in from another endpoint — but they are mutually exclusive
 * per row, so the width is stated rather than derived from the list, which would otherwise leave
 * an empty button's worth of space on every row of every section.
 */
const ACTIONS_COLUMN_WIDTH = "68px";

const getFileType = (doc) => {
  if (doc.documentUrl) {
    const ext = doc.documentUrl.split("?")[0].split(".").pop()?.toUpperCase();
    if (ext && ext.length <= 5) return ext;
  }
  if (doc.fileName) {
    const ext = doc.fileName.split(".").pop()?.toUpperCase();
    if (ext && ext.length <= 5) return ext;
  }
  const mimeMap = {
    "application/pdf": "PDF",
    "image/jpeg": "JPG",
    "image/png": "PNG",
    "application/msword": "DOC",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
      "DOCX",
  };
  return doc.mimeType ? mimeMap[doc.mimeType] || "-" : "-";
};

const getStatusPillColors = (status) => {
  if (!status) return PILL_COLORS.docUnknown;
  const map = {
    NEW: PILL_COLORS.docNew,
    IN_REVIEW: PILL_COLORS.docInProgress,
    REVIEWED: PILL_COLORS.docReviewed,
    CLOSED: PILL_COLORS.docClosed,
    UPLOADED: PILL_COLORS.docUploaded,
    SYSTEM_GENERATED: PILL_COLORS.docSystemGenerated,
    REFERRAL: PILL_COLORS.docReferral,
  };
  return map[status.toUpperCase()] || PILL_COLORS.docUnknown;
};

export const DocumentList = ({
  documents = [],
  isLoading = false,
  filters,
  selection,
  onUploadClick,
  onView,
  onDelete,
  onPrint,
  onDownload,
  onEdit,
  onEditCommunicationsDocument,
  isCommunicationsDocumentRow,
  onChangeStatus,
  onReview,
  onBulkChangeStatus,
  onSendEmail,
  onBulkDownload,
  onViewSourceDocument,
  failedSourceLabels = [],
  onRetrySources,
  documentType = "",
  showDocumentType = false,
  currentPage = 1,
  totalRecords = 0,
  rowsPerPage = 20,
  rowsOptions,
  rowsValue,
  onRowsChange,
  onPageChange,
}) => {
  const { data: allDocumentTypes = [] } = useDocumentTypes();
  const activeDocType = allDocumentTypes.find((dt) => dt.name === documentType);
  const [staffMembers, setStaffMembers] = useState([]);

  const tenant =
    useSelector((state) => state.auth.user?.tenant) ||
    localStorage.getItem("kinesin-tenant");

  const fetchStaffMembers = useCallback(async () => {
    try {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.get(
        `${tenant}${STAFF_MEMBERS_CONTROLER_URL}`
      );
      setStaffMembers(response.data || []);
    } catch (error) {
      console.error("Error fetching staff members:", error);
    }
  }, [tenant]);

  useEffect(() => {
    fetchStaffMembers();
  }, [fetchStaffMembers]);

  const formatDocType = (type) => {
    if (!type) return "";
    if (activeDocType?.label) return activeDocType.label;
    return formatDocumentTypeLabel(type);
  };

  const getDocTypeIcon = (type) => {
    if (!type) return null;
    const Icon = getDocumentTypeIcon(type, activeDocType?.icon);
    return <Icon size={20} />;
  };

  const getReviewerName = useCallback(
    (doc) => {
      if (doc.reviewedById && staffMembers.length > 0) {
        const staff = staffMembers.find((s) => s.id === doc.reviewedById);
        if (staff)
          return (
            `${staff.firstName || ""} ${staff.lastName || ""}`.trim() || "-"
          );
      }
      if (doc.reviewedBy?.firstName || doc.reviewedBy?.lastName) {
        return (
          `${doc.reviewedBy.firstName || ""} ${
            doc.reviewedBy.lastName || ""
          }`.trim() || "-"
        );
      }
      if (doc.reviewerStaff?.firstName || doc.reviewerStaff?.lastName) {
        return (
          `${doc.reviewerStaff.firstName || ""} ${
            doc.reviewerStaff.lastName || ""
          }`.trim() || "-"
        );
      }
      return "-";
    },
    [staffMembers]
  );

  const columns = useMemo(() => {
    const cols = [
      {
        id: "documentName",
        header: "Document Name",
        width: "300px",
        render: (doc) => doc.name || doc.fileName || "Untitled",
        getTooltipText: (doc) => doc.name || doc.fileName || "Untitled",
      },
    ];

    if (showDocumentType) {
      cols.push({
        id: "documentType",
        header: "Document Type",
        width: "170px",
        /*
         * A row merged in from another endpoint names its own type ("Discharge summary", "Baby's
         * discharge summary", "Sick certificate"): it has no uploaded document type to format,
         * and the baby/mother distinction (KIN-1949) is carried in that label.
         */
        render: (doc) =>
          doc.documentTypeLabel ??
          (doc.documentType ? formatDocumentTypeLabel(doc.documentType) : "-"),
      });
    }

    cols.push(
      {
        id: "fileType",
        header: "File Type",
        width: "105px",
        /*
         * Only an upload is a file of the practice's own. A discharge summary is an HL7 message
         * rendered through the hospital's stylesheet, so a file-type pill on it would be an
         * invention — but a DEASP cert really is downloadable as a PDF (KIN-2102).
         */
        render: (doc) => {
          if (isUploadedDocumentRow(doc))
            return <Pill {...PILL_COLORS.method}>{getFileType(doc)}</Pill>;

          if (isDeaspCertificateRow(doc))
            return <Pill {...PILL_COLORS.method}>PDF</Pill>;

          return "—";
        },
      },
      {
        id: "reviewer",
        header: "Reviewer",
        width: "170px",
        /*
         * Review is something a practice does to an uploaded document. A row merged in from
         * another endpoint has no reviewer to name, so it gets the same deliberate em dash the
         * other inapplicable cells carry rather than a hyphen that reads as "none recorded".
         */
        render: (doc) => (isUploadedDocumentRow(doc) ? getReviewerName(doc) : "—"),
        getTooltipText: (doc) =>
          isUploadedDocumentRow(doc) ? getReviewerName(doc) : "—",
      },
      {
        id: "dateUploaded",
        header: "Date Uploaded",
        width: "170px",
        render: (doc) =>
          formatDocumentDate(
            doc.created || doc.uploadedDate || doc.createdDate
          ),
      },
      {
        id: "status",
        header: "Status",
        width: "minmax(140px, 1fr)",
        /*
         * Status here is the review workflow an upload goes through. A discharge summary has none
         * of that, and an "Unknown" pill on it would read as a document in an unknown state rather
         * than as a document the state does not apply to.
         *
         * A DEASP cert does have a status, just a different one — how far the submission got with
         * Healthlink and DEASP. It was being dropped (KIN-2102), which hid a rejected claim.
         */
        render: (doc) => {
          if (isUploadedDocumentRow(doc))
            return (
              <Pill {...getStatusPillColors(doc.status)}>
                {formatStatusLabel(doc.status)}
              </Pill>
            );

          const deaspStatus = getDeaspCertificateStatusDisplay(doc.deaspStatus);

          if (deaspStatus)
            return <Pill {...deaspStatus.colors}>{deaspStatus.label}</Pill>;

          return "—";
        },
      }
    );

    return cols;
  }, [showDocumentType, getReviewerName]);

  /*
   * Every action but "View" is an operation on an uploaded document — the documents endpoint owns
   * the blob to download, the review workflow, the record to edit or delete. A row merged into
   * "All Documents" from another endpoint (KIN-2068) offers none of them: an action that silently
   * did nothing, or a Delete that cannot delete, would be worse than its absence. It gets the one
   * action that applies — opening it in the viewer its own section uses, where that viewer's own
   * Print button sits.
   */
  const rowActions = useMemo(
    () => [
      {
        label: "View",
        icon: Eye,
        primary: true,
        visibleWhen: (doc) => !isUploadedDocumentRow(doc),
        onClick: (doc) => onViewSourceDocument?.(doc),
      },
      {
        label: "Open preview",
        icon: Eye,
        visibleWhen: isUploadedDocumentRow,
        onClick: (doc) => onView?.(doc.id, doc.patient?.id || doc.patientId),
      },
      {
        label: "Change status",
        icon: Pencil,
        visibleWhen: isUploadedDocumentRow,
        onClick: (doc) => onChangeStatus?.(doc),
      },
      {
        label: "Download",
        icon: Download,
        visibleWhen: isUploadedDocumentRow,
        onClick: (doc) => onDownload?.(doc),
        primary: true,
      },
      {
        label: "Print",
        icon: Printer,
        visibleWhen: isUploadedDocumentRow,
        onClick: (doc) => onPrint?.(doc),
      },
      {
        label: "Send Email",
        icon: Mail,
        visibleWhen: isUploadedDocumentRow,
        onClick: (_doc) => onSendEmail?.(),
      },
      {
        label: "Review",
        icon: Eye,
        visibleWhen: isUploadedDocumentRow,
        onClick: (doc) => onReview?.(doc),
      },
      /*
       * A sick-cert/letter row opens the richer Communications editor — RichEditor content,
       * regenerated PDF on save — instead of the metadata-only one every other uploaded
       * document gets (KIN-1833-adjacent follow-up: these two started out without any content
       * edit from this tab at all).
       */
      {
        label: "Edit",
        icon: Pencil,
        visibleWhen: (doc) =>
          isUploadedDocumentRow(doc) && !!isCommunicationsDocumentRow?.(doc),
        onClick: (doc) => onEditCommunicationsDocument?.(doc),
      },
      {
        label: "Edit",
        icon: Pencil,
        visibleWhen: (doc) =>
          isUploadedDocumentRow(doc) && !isCommunicationsDocumentRow?.(doc),
        onClick: (doc) => onEdit?.(doc),
      },
      {
        label: "Delete",
        icon: Trash2,
        visibleWhen: isUploadedDocumentRow,
        onClick: (doc) => onDelete?.(doc),
      },
    ],
    [
      onView,
      onViewSourceDocument,
      onChangeStatus,
      onDownload,
      onPrint,
      onSendEmail,
      onReview,
      onEdit,
      onEditCommunicationsDocument,
      isCommunicationsDocumentRow,
      onDelete,
    ]
  );

  const bulkActions = useMemo(
    () => [
      { label: "Download", icon: Download, onClick: onBulkDownload },
      { label: "Change status", icon: Pencil, onClick: onBulkChangeStatus },
      { label: "Send email", icon: Mail, onClick: onSendEmail },
    ],
    [onBulkDownload, onBulkChangeStatus, onSendEmail]
  );

  const selectionAdapter = useMemo(
    () => ({
      selectedIds: new Set(selection.selectedIds || []),
      selectedCount: selection.selectedCount ?? 0,
      isSelected: selection.isSelected,
      toggleSelection: (id, row, checked) => {
        if (checked !== undefined) {
          const currentlySelected = selection.isSelected(id);
          if (checked && !currentlySelected) selection.toggleSelection(id, row);
          else if (!checked && currentlySelected)
            selection.toggleSelection(id, row);
        } else {
          selection.toggleSelection(id, row);
        }
      },
      selectAll: selection.selectAll,
      deselectAll: selection.deselectAll,
    }),
    [selection]
  );

  /*
   * A source of "All Documents" that failed to load (KIN-2068). With rows on screen it is a
   * banner above them; with none it replaces the empty state entirely, because "No documents
   * found" is a statement about the patient's record and we do not have the record.
   */
  const failedSourcesLabel = joinSourceLabels(failedSourceLabels);
  const hasFailedSource = failedSourceLabels.length > 0;
  const hasOnlyFailedSources = hasFailedSource && documents.length === 0;

  const hasActiveFilters =
    filters.searchQuery?.length > 0 ||
    filters.statusFilter?.length > 0 ||
    filters.priorityFilter?.length > 0;

  return (
    <div className={styles.documentList}>
      <div className={styles.header}>
        <p className={styles.title}>
          {getDocTypeIcon(documentType)}
          {formatDocType(documentType) || "Documents"}
        </p>
        <Button variant="text" onClick={onUploadClick} endIcon={Plus}>
          Add new document
        </Button>
      </div>

      {!!hasFailedSource && !hasOnlyFailedSources && (
        <DataLoadError
          label={failedSourcesLabel}
          text="This list may be incomplete — do not rely on it as a record of what is absent."
          onRetry={onRetrySources}
        />
      )}

      <DataTable
        columns={columns}
        data={documents}
        getRowId={(row) => row.id}
        isLoading={isLoading}
        isError={hasOnlyFailedSources}
        errorLabel={failedSourcesLabel}
        onRetry={onRetrySources}
        isRowSelectable={isUploadedDocumentRow}
        actionsColumnWidth={ACTIONS_COLUMN_WIDTH}
        emptyIllustration={hasActiveFilters ? undefined : emptyDocs}
        emptyTitle="No documents found"
        emptyText={
          hasActiveFilters
            ? "Try adjusting your search or filters to find what you're looking for."
            : "Once the patient receives a document, it will appear here."
        }
        search={{
          value: filters.searchQuery,
          onChange: filters.setSearch,
          placeholder: "Search document name",
        }}
        sort={{
          field: filters.sortField,
          direction: filters.sortDirection,
          onChange: filters.onSortChange,
        }}
        sortOptions={[
          {
            field: "created",
            direction: "desc",
            label: "Date (Newest first)",
            icon: ArrowDownNarrowWide,
          },
          {
            field: "created",
            direction: "asc",
            label: "Date (Oldest first)",
            icon: ArrowUpNarrowWide,
          },
          {
            field: "name",
            direction: "asc",
            label: "Name (A–Z)",
            icon: ArrowUpNarrowWide,
          },
          {
            field: "name",
            direction: "desc",
            label: "Name (Z–A)",
            icon: ArrowDownNarrowWide,
          },
        ]}
        filters={{
          value: { status: filters.statusFilter },
          onChange: (groupId, value) => {
            if (groupId === "status") filters.onStatusFilterChange(value);
          },
        }}
        filtersConfig={FILTER_GROUPS}
        selectable
        selection={selectionAdapter}
        topActions={[]}
        rowActions={rowActions}
        bulkActions={bulkActions}
        currentPage={currentPage}
        rowsPerPage={rowsPerPage}
        totalCount={totalRecords}
        rowsOptions={rowsOptions}
        rowsValue={rowsValue}
        onRowsChange={onRowsChange}
        onPageChange={(page) => onPageChange?.(page)}
      />
    </div>
  );
};
